import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { onValue, ref } from 'firebase/database'
import { db } from '../lib/firebase.js'
import { getPlayStyle, headToHeadStatus, matchPlayResult, parsFor, rankTeams, fmtVsPar, netAvailableFor, netScoresFor, strokeIndexFor } from '../lib/scoring.js'
import CLUB from '../config/club.js'
import { PBCC_PREMIUM } from '../config/siteMode.js'
import { pickPremiumCurrentEventId } from '../lib/pickCurrentEvent.js'
import { boardTitle } from '../lib/boardTitle.js'
import { TV_BG_GREEN, TV_CARD_WHITE, TV_CARD_TEXT } from '../config/tvTheme.js'
import ScrambleTable from './ScrambleTable.jsx'
// TEST/premium gold for headers/accents; club/live keep the club accent color.
const TV_GOLD = PBCC_PREMIUM ? '#C2A24E' : CLUB.accentColor
// Card edge on the TV boards: on the premium/TEST theme the cream cards sit on
// deep green with only a thin GOLD hairline — never the old light-gray/green
// outline that tinted them. Club/live keep their original light-gray edge.
const TV_CARD_EDGE = PBCC_PREMIUM ? '#C2A24E80' : '#E5E7EB'
import { lastNames } from '../lib/teamName.js'
import TvScroll from './TvScroll.jsx'

// Big-screen / clubhouse-TV leaderboard. Match-play only by design — one
// row per pairing, large readable text, no admin chrome (no QR, no NET
// toggle, no through/gross columns). Auto-scrolls when content exceeds
// the viewport so the operator can mount the URL on a TV and forget it.
//
// URL: /tv          → auto-pick most recent open non-demo tournament
//      /tv?t=ID     → pin to a specific tournament
//
// Also rendered inside the admin program as the "TV View" tab — the admin
// passes `tournamentIdProp` to pin the view to the tournament they're in
// and `embedded` to drop the full-screen kiosk container so it fits the
// admin layout. Same component, same scroll behavior, same live data.

export default function TvLeaderboard({ tournamentIdProp = null, embedded = false, fill = false } = {}) {
  const [params] = useSearchParams()
  const tournamentIdHint = tournamentIdProp ?? params.get('t')
  const [allTournaments, setAllTournaments] = useState({})
  const [tournament, setTournament] = useState(null)

  useEffect(() => {
    const off = onValue(ref(db, 'tournaments'), (snap) => setAllTournaments(snap.val() || {}))
    return () => off()
  }, [])

  const activeId = useMemo(() => {
    if (tournamentIdHint) return tournamentIdHint
    // TEST: deterministic, flights/stage-aware pick — never the empty qualifying.
    if (PBCC_PREMIUM) return pickPremiumCurrentEventId(allTournaments)
    const entries = Object.entries(allTournaments).filter(
      ([, v]) => !v?.info?.isDemo && v?.info?.status !== 'closed',
    )
    if (entries.length === 0) return null
    entries.sort((a, b) => (b[1]?.info?.createdAt || 0) - (a[1]?.info?.createdAt || 0))
    return entries[0][0]
  }, [tournamentIdHint, allTournaments])

  useEffect(() => {
    if (!activeId) { setTournament(null); return }
    const off = onValue(ref(db, `tournaments/${activeId}`), (snap) => setTournament(snap.val()))
    return () => off()
  }, [activeId])

  if (!activeId) return <TvShell embedded={embedded} fill={fill}><Empty msg="No active tournament." /></TvShell>
  if (!tournament) return <TvShell embedded={embedded} fill={fill}><Empty msg="Loading…" /></TvShell>

  const flights = Object.entries(tournament.flights || {})
    .filter(([fid]) => !fid.startsWith('_'))
    .map(([id, v]) => ({ id, ...v }))

  // Scroll-speed is admin-controlled per tournament. Default 'slow'.
  const scrollSpeed = tournament?.tvSettings?.scrollSpeed || 'slow'
  // Admin-controlled GROSS/NET display mode (tvSettings.netMode). Only takes
  // effect when net data exists; default gross.
  const netMode = !!tournament?.tvSettings?.netMode && netAvailableFor(tournament)
  const strokeIndex = strokeIndexFor(tournament)

  return (
    <TvShell tournament={tournament} embedded={embedded} fill={fill}>
      <TvScroll disabled={embedded} speed={scrollSpeed} innerClassName="px-10 py-6 space-y-8" style={{ fontFamily: 'Marcellus, Georgia, serif' }}>
        {flights.map((f) => (
          <TvFlight key={f.id} tournament={tournament} flight={f} netMode={netMode} strokeIndex={strokeIndex} />
        ))}
        {flights.length === 0 ? <Empty msg="No flights configured." /> : null}
      </TvScroll>
    </TvShell>
  )
}

// Outer shell. Standalone /tv mode is full-screen kiosk; the embedded admin-
// tab mode uses a bounded height so the surrounding admin nav stays visible
// and the auto-scroll has a viewport to scroll within.
function TvShell({ tournament, children, embedded, fill = false }) {
  // Standalone uses position:fixed inset:0 so the container ALWAYS covers
  // exactly the viewport — independent of any parent wrappers (TrialGate,
  // WithBanner, BrowserRouter, etc). h-screen alone wasn't enough because
  // even with html/body/#root at height:100%, intermediate flex/min-h-full
  // wrappers could let the container grow with content. Fixed positioning
  // is the kiosk-display contract: full viewport, parent-independent. The
  // inner flex layout (header → flex-1 scroll area) then sizes correctly
  // and AutoScrollList's wrapper has a bounded height that content can
  // overflow, which is what makes scrolling actually fire.
  // `fill` (used by the split-screen layout): full-size chrome + scrolling like
  // the standalone view, but container-relative (w-full h-full) instead of
  // fixed inset-0 so it can occupy one pane of a split. embedded/standalone
  // paths are unchanged.
  const containerClass = embedded
    ? 'w-full flex flex-col rounded-xl overflow-hidden'
    : fill
    ? 'w-full h-full flex flex-col overflow-hidden'
    : 'fixed inset-0 flex flex-col overflow-hidden'
  const containerStyle = embedded
    ? { backgroundColor: TV_BG_GREEN, color: '#FFFFFF', height: 'min(78vh, calc(100vh - 200px))' }
    : fill
    ? { backgroundColor: TV_BG_GREEN, color: '#FFFFFF', height: '100%' }
    : { backgroundColor: TV_BG_GREEN, color: '#FFFFFF', zIndex: 1 }
  return (
    <div className={containerClass} style={containerStyle}>
      {/* Marcellus — same scrolling font as the GPS test-map boxes. */}
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Marcellus&display=swap');`}</style>
      <header className={`${embedded ? 'px-6 pt-4 pb-2' : 'px-10 pt-6 pb-3'} flex items-center gap-5 shrink-0`}
              style={{ borderBottom: `2px solid ${TV_GOLD}40` }}>
        <div className="rounded-xl p-2">
          <img
            src={CLUB.logoLight}
            alt={CLUB.name}
            style={{ height: embedded ? 84 : 132, objectFit: 'contain', display: 'block' }}
          />
        </div>
        <div className="flex-1 min-w-0" style={{ fontFamily: 'Marcellus, Georgia, serif' }}>
          <div className={`uppercase tracking-[0.25em] font-bold ${embedded ? 'text-sm' : 'text-sm'}`}
               style={{ color: TV_GOLD }}>
            Live Leaderboard
          </div>
          <h1 className={`font-black leading-tight truncate ${embedded ? 'text-2xl' : 'text-4xl'}`}
              style={{ color: TV_GOLD }}>
            {boardTitle(tournament?.info?.name) || ''}
          </h1>
        </div>
      </header>
      <div className="flex-1 min-h-0">{children}</div>
    </div>
  )
}


function TvFlight({ tournament, flight, netMode = false, strokeIndex }) {
  // Scramble flights (e.g. Flight 12) render the score table, never VS pairings.
  if (flight.scramble) return <ScrambleTable data={flight.scramble} flightName={flight.info?.name} />
  const playStyle = getPlayStyle(tournament, flight)
  const rawTeams = Object.entries(flight.teams || {}).map(([id, v]) => ({ id, ...v }))
  // Per-hole NET mode: swap gross scores for net best-ball (same shape), then
  // run the SAME ranking / match logic — mirrors the page leaderboard.
  const teams = netMode ? rawTeams.map((t) => ({ ...t, scores: netScoresFor(t, strokeIndex) })) : rawTeams

  // Stroke-play tournaments (Scramble, Stroke Play Individual, Stroke Big
  // Team, etc.) get a leaderboard list sorted by score. Whenever the
  // tournament has scramble handicaps on, sort by net.
  if (playStyle !== 'match') {
    const scrambleNet =
      tournament?.info?.format === 'Scramble' &&
      !!tournament?.info?.scrambleHandicapsEnabled
    // In net mode the strokes are already baked into scores → rank by vs-par.
    const useNet = netMode ? false : scrambleNet
    const pars = parsFor(tournament)
    const ranked = rankTeams(teams, useNet, pars)
    return (
      <section>
        <FlightHeader
          flight={flight}
          subtitle={`${ranked.length} team${ranked.length === 1 ? '' : 's'}${(netMode || scrambleNet) ? ' · NET' : ''}`}
        />
        <ul className="mt-3 space-y-3">
          {ranked.length === 0 ? (
            <li className="text-2xl text-white/60 italic">No teams yet.</li>
          ) : (
            ranked.map((r, i) => (
              <TvStrokeRow key={r.id} rank={i + 1} row={r} useNet={useNet} />
            ))
          )}
        </ul>
      </section>
    )
  }

  const matches = buildMatches(teams)

  return (
    <section>
      <FlightHeader flight={flight} />
      <ul className="mt-3 space-y-3">
        {matches.length === 0 ? (
          <li className="text-2xl text-white/60 italic">No matches yet.</li>
        ) : (
          matches.map((m) => <TvMatchRow key={m.key} match={m} />)
        )}
      </ul>
    </section>
  )
}

// Stroke-play / Scramble row for the TV leaderboard. Big rank, big team
// name, big score, FINAL pill instead of THRU when team has scored 18.
function TvStrokeRow({ rank, row, useNet }) {
  const GREEN = '#22C55E'
  const GOLD = PBCC_PREMIUM ? '#C2A24E' : '#FFFFFF'
  const CRIMSON = '#B10C0C' // readable text on the cream bar
  const isFinal = row.thru >= 18
  const score = useNet ? row.net : row.gross
  const scoreLabel = score > 0 ? score : '—'
  return (
    <li
      className="rounded-2xl px-8 py-5 flex items-center gap-6"
      style={{
        backgroundColor: TV_CARD_WHITE,
        border: `1.5px solid ${rank === 1 ? GOLD : TV_CARD_EDGE}`,
      }}>
      <div className="text-4xl font-black w-12 text-right" style={{ color: TV_CARD_TEXT }}>{rank}</div>
      <div className="flex-1 min-w-0">
        <div className="text-3xl font-black truncate" style={{ color: TV_CARD_TEXT }}>{lastNames(row.info?.name)}</div>
        <div className="text-base truncate" style={{ color: TV_CARD_TEXT }}>{(row.info?.players || []).join(' · ')}</div>
      </div>
      <div className="text-right shrink-0">
        {isFinal ? (
          <div
            className="inline-flex items-center rounded-md px-3 py-1 text-sm font-extrabold uppercase tracking-wider mb-1"
            style={{ backgroundColor: GREEN, color: '#0A2C18' }}>
            FINAL
          </div>
        ) : (
          <div className="text-sm font-bold uppercase tracking-wider mb-1" style={{ color: TV_CARD_TEXT }}>
            THRU {row.thru}
          </div>
        )}
        <div className="text-3xl font-black whitespace-nowrap" style={{ color: TV_CARD_TEXT }}>
          {scoreLabel}
          <span className="text-lg font-bold ml-2" style={{ color: TV_CARD_TEXT }}>
            {row.gross > 0 ? fmtVsPar(row.vsPar) : ''}
          </span>
        </div>
      </div>
    </li>
  )
}

function FlightHeader({ flight, subtitle }) {
  return (
    <div className="flex items-baseline gap-4">
      <h2 className="text-3xl font-black" style={{ color: TV_GOLD }}>
        {flight.info?.name || 'Flight'}
      </h2>
      {subtitle ? (
        <span className="text-sm uppercase tracking-widest" style={{ color: '#FFFFFFAA' }}>
          {subtitle}
        </span>
      ) : null}
    </div>
  )
}

// Single big row: TEAM A NAME — STATUS — TEAM B NAME. Status flips to a
// "FINAL — winner wins X&Y" banner once clinched.
function TvMatchRow({ match }) {
  const { teamA, teamB, leaderGap, played, result } = match
  const GREEN = '#22C55E'
  const RED = '#F87171'
  const GOLD = TV_GOLD
  const DIM = '#9CA3AF'

  if (!teamB) {
    return (
      <li className="rounded-xl px-6 py-4 text-2xl font-bold"
          style={{ backgroundColor: TV_CARD_WHITE, border: `1px solid ${TV_CARD_EDGE}` }}>
        <span style={{ color: TV_CARD_TEXT }}>{lastNames(teamA.info?.name)}</span>
        <span className="ml-3" style={{ color: TV_CARD_TEXT }}>— no opponent</span>
      </li>
    )
  }

  const isFinal = !!result?.final
  const halved = isFinal && result?.winner === 'tie'
  const notStarted = played === 0
  const allSquare = leaderGap === 0

  // Color rules:
  //   live: leader green, trailer red, AS shows gold center
  //   final: winning team green + 🏆, losing team dim red, halved both gold
  // Default (non-status) text is crimson so it stays readable on the cream bar.
  // The green/red/gold status colors below are UNCHANGED — they override this.
  let aColor = '#B10C0C'
  let bColor = '#B10C0C'
  let aPrefix = ''
  let bPrefix = ''
  let centerText = ''
  let centerColor = '#B10C0C'
  let borderTint = TV_CARD_EDGE

  if (isFinal) {
    if (halved) {
      aColor = '#000000'; bColor = '#000000' // gold text -> black (readable on cream)
      centerText = `FINAL — Halved (AS thru 18)`
      centerColor = '#000000'
      borderTint = GOLD
    } else {
      aColor = GREEN
      bColor = '#A78B8B'
      aPrefix = '🏆 '
      centerText = `FINAL — ${lastNames(teamA.info?.name)} wins ${result.resultText}`
      centerColor = GREEN
      // TEST: no light-green outline on the cream card — use the gold hairline.
      // Club/live keep the green winner edge.
      borderTint = PBCC_PREMIUM ? TV_CARD_EDGE : GREEN
    }
  } else if (notStarted) {
    aColor = DIM; bColor = DIM
    centerText = 'Not started'
    centerColor = DIM
  } else if (allSquare) {
    centerText = `ALL SQUARE THRU ${played}`
    centerColor = '#000000' // gold "AS" text -> black (readable on cream)
  } else {
    aColor = GREEN
    bColor = RED
    aPrefix = '▲ '
    bPrefix = '▼ '
    centerText = `${lastNames(teamA.info?.name)} ${leaderGap}UP THRU ${played}`
    centerColor = GREEN
  }

  // TV white cards: ALL text dark green (overrides the status colors above; the
  // ▲/▼ prefixes still carry the leader/trailer cue).
  aColor = TV_CARD_TEXT
  bColor = TV_CARD_TEXT
  centerColor = TV_CARD_TEXT

  return (
    <li
      className="rounded-2xl px-8 py-5"
      style={{
        backgroundColor: TV_CARD_WHITE,
        border: `1.5px solid ${borderTint}`,
      }}>
      <div className="flex items-center gap-6">
        <div className="flex-1 min-w-0 text-right">
          <div className="text-3xl font-black truncate" style={{ color: aColor }}>
            {aPrefix}{lastNames(teamA.info?.name)}
          </div>
        </div>
        <div className="text-2xl font-extrabold uppercase tracking-wider whitespace-nowrap text-center"
             style={{ color: centerColor, minWidth: 280 }}>
          vs
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-3xl font-black truncate" style={{ color: bColor }}>
            {bPrefix}{lastNames(teamB.info?.name)}
          </div>
        </div>
      </div>
      <div className="mt-2 text-2xl font-extrabold text-center uppercase tracking-wider"
           style={{ color: centerColor }}>
        {centerText}
      </div>
    </li>
  )
}

function buildMatches(teams) {
  const byId = Object.fromEntries(teams.map((t) => [t.id, t]))
  const seen = new Set()
  const matches = []
  for (const t of teams) {
    if (seen.has(t.id)) continue
    const oppId = t.info?.opponentId
    if (oppId && byId[oppId] && oppId !== t.id) {
      seen.add(t.id); seen.add(oppId)
      const opp = byId[oppId]
      const h2h = headToHeadStatus(t, opp)
      const gap = h2h.myWins - h2h.oppWins
      let teamA, teamB, leaderGap, aWins, bWins
      if (gap >= 0) { teamA = t; teamB = opp; leaderGap = gap; aWins = h2h.myWins; bWins = h2h.oppWins }
      else          { teamA = opp; teamB = t; leaderGap = -gap; aWins = h2h.oppWins; bWins = h2h.myWins }
      const result = matchPlayResult({ myWins: aWins, oppWins: bWins, played: h2h.played })
      matches.push({ key: `${t.id}-${opp.id}`, teamA, teamB, leaderGap, played: h2h.played, result })
    } else {
      seen.add(t.id)
      matches.push({ key: `solo-${t.id}`, teamA: t, teamB: null, leaderGap: 0, played: 0, result: null })
    }
  }
  // Finals first, then by lead size, then holes played.
  matches.sort((a, b) => {
    const af = a.result?.final ? 1 : 0
    const bf = b.result?.final ? 1 : 0
    if (af !== bf) return bf - af
    if (b.leaderGap !== a.leaderGap) return b.leaderGap - a.leaderGap
    return b.played - a.played
  })
  return matches
}

function Empty({ msg }) {
  return (
    <div className="h-full flex items-center justify-center p-12">
      <div className="text-center">
        <div className="text-6xl mb-3">⛳</div>
        <p className="text-3xl font-bold">{msg}</p>
      </div>
    </div>
  )
}
